import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import AlertMessage from '../components/common/AlertMessage';
import LocationSelector from '../components/forms/LocationSelector';
import { Edit, MapPin, Calendar, Star, Mail, Music, Save, X } from 'lucide-react';
import { apiService } from '../services/api';
import { GENRE_OPTIONS, toggleGenreSelection } from '../shared/genres';
import { AVAILABILITY_OPTIONS, toggleAvailabilitySelection } from '../shared/availability';
import { DEFAULT_AVATAR_URL, getInstrumentAvatarUrl, INSTRUMENT_OPTIONS } from '../shared/avatars';

interface ProfileData {
  id: string;
  name: string;
  instrument: string;
  skillLevel: string;
  created?: string;
  birthYear?: number;
  address?: string;
  genres: string[];
  availability: string[];
}

const ProfilePage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [remoteProfile, setRemoteProfile] = useState<ProfileData | null>(null);
  const [isFetchingProfile, setIsFetchingProfile] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    instrument: user?.instrument || '',
      skillLevel: user?.skillLevel || '',
      birthYear: user?.birthYear,
      genres: user?.genres ?? [],
      availability: user?.availability ?? [],
      address: user?.address || '',
      location: user?.location,
  });
  const maxGenres = 3;
  
  const isCurrentUser = !id || id === user?.id;
  const profileData = isCurrentUser ? user : remoteProfile;

  const normalizeGenres = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];
    return value
      .filter((genre) => typeof genre === 'string')
      .map((genre) => genre.trim())
      .filter((genre) => genre.length > 0);
  };

  const getNumericValue = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  };

  const normalizeProfile = (value: any, fallbackId: string): ProfileData => ({
    id: String(value?.id ?? fallbackId),
    name: String(value?.name ?? value?.username ?? value?.email ?? 'Unknown musician'),
    instrument: String(value?.instrument ?? ''),
    skillLevel: String(value?.skillLevel ?? ''),
    created:
      typeof value?.created === 'string'
        ? value.created
        : typeof value?.createdAt === 'string'
          ? value.createdAt
          : undefined,
    birthYear: getNumericValue(value?.birthYear) ?? undefined,
    address: typeof value?.address === 'string' ? value.address : '',
    genres: normalizeGenres(value?.genres),
    availability: normalizeGenres(value?.availability),
  });

  const getMemberSinceLabel = (createdValue: unknown): string => {
    if (typeof createdValue !== 'string') return 'Member since unavailable';
    const createdDate = new Date(createdValue);
    if (Number.isNaN(createdDate.getTime())) return 'Member since unavailable';
    const monthYear = createdDate.toLocaleString('en-US', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });
    return `Member since ${monthYear}`;
  };

  const getAgeFromBirthYear = (birthYear?: number): number | null => {
    if (typeof birthYear !== 'number' || !Number.isFinite(birthYear)) return null;
    const currentYear = new Date().getFullYear();
    const age = currentYear - birthYear;
    if (age < 0 || age > 120) return null;
    return age;
  };

  useEffect(() => {
    if (isCurrentUser) {
      setRemoteProfile(null);
      setIsFetchingProfile(false);
      setProfileError(null);
      setIsEditing(false);
      return;
    }

    let isMounted = true;
    const loadProfile = async () => {
      if (!id) return;
      setIsFetchingProfile(true);
      setProfileError(null);
      try {
        const response = await apiService.getUserById(id);
        if (isMounted) {
          setRemoteProfile(normalizeProfile(response, id));
        }
      } catch (error) {
        if (isMounted) {
          setRemoteProfile(null);
          setProfileError(error instanceof Error ? error.message : 'Unable to load profile.');
        }
      } finally {
        if (isMounted) {
          setIsFetchingProfile(false);
        }
      }
    };

    loadProfile();
    return () => {
      isMounted = false;
    };
  }, [id, isCurrentUser]);

  useEffect(() => {
    if (!isCurrentUser || isEditing || !user) return;
    setFormData({
      name: user.name || '',
      instrument: user.instrument || '',
      skillLevel: user.skillLevel || '',
      birthYear: user.birthYear,
      genres: user.genres ?? [],
      availability: user.availability ?? [],
      address: user.address || '',
      location: user.location,
    });
  }, [isCurrentUser, isEditing, user]);
  
  if (!profileData) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        {profileError && (
          <div className="mb-4">
            <AlertMessage variant="error">{profileError}</AlertMessage>
          </div>
        )}
        <p className="text-xl text-gray-400">
          {isFetchingProfile ? 'Loading profile...' : 'User not found'}
        </p>
      </div>
    );
  }
  
  const handleEditToggle = async () => {
    if (isEditing) {
      // Save changes
      if (isCurrentUser && user) {
        const genreCount = formData.genres?.length ?? 0;
        if (genreCount < 1 || genreCount > maxGenres) {
          setProfileError(`Select between 1 and ${maxGenres} genres before saving.`);
          return;
        }
        const availabilityCount = formData.availability?.length ?? 0;
        if (availabilityCount < 1 || availabilityCount > maxGenres) {
          setProfileError(`Select between 1 and ${maxGenres} availability options before saving.`);
          return;
        }
        try {
          setProfileError(null);
          await updateProfile(formData);
        } catch (error) {
          setProfileError(error instanceof Error ? error.message : 'Profile update failed. Please try again.');
          return;
        }
      }
    }
    setProfileError(null);
    setIsEditing(!isEditing);
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'birthYear') {
      if (value.trim() === '') {
        setFormData({
          ...formData,
          birthYear: undefined,
        });
        return;
      }
      const parsed = Number(value);
      setFormData({
        ...formData,
        birthYear: Number.isFinite(parsed) ? parsed : undefined,
      });
      return;
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleLocationSelect = (location: { lat: number; lng: number }, address: string) => {
    setFormData((prev) => ({
      ...prev,
      location,
      address,
    }));
  };
  
  const handleCancelEdit = () => {
    // Reset form data to current user data
    if (user) {
      setFormData({
        name: user.name,
        instrument: user.instrument,
        skillLevel: user.skillLevel,
        birthYear: user.birthYear,
        genres: user.genres ?? [],
        availability: user.availability ?? [],
        address: user.address || '',
        location: user.location,
      });
    }
    setProfileError(null);
    setIsEditing(false);
  };
  const handleGenreToggle = (genre: string) => {
    setFormData((prev) => ({
      ...prev,
      genres: toggleGenreSelection(prev.genres ?? [], genre, maxGenres),
    }));
  };
  const handleAvailabilityToggle = (availability: string) => {
    setFormData((prev) => ({
      ...prev,
      availability: toggleAvailabilitySelection(prev.availability ?? [], availability, maxGenres),
    }));
  };

  // Function to determine the skill level stars
  const getSkillStars = (level: string) => {
    switch (level) {
      case 'Beginner':
        return 1;
      case 'Intermediate':
        return 2;
      case 'Advanced':
        return 3;
      case 'Professional':
        return 4;
      default:
        return 0;
    }
  };

  const skillStars = getSkillStars(isEditing ? formData.skillLevel : profileData.skillLevel);
  const displayedGenres = isEditing ? formData.genres : profileData.genres;
  const displayedBirthYear = isEditing ? formData.birthYear : profileData.birthYear;
  const displayedAddress = isEditing ? formData.address : profileData.address;
  const displayedAvailability = isEditing ? formData.availability : profileData.availability;
  const displayedAge = getAgeFromBirthYear(displayedBirthYear);
  const avatarInstrument = isEditing ? formData.instrument : profileData.instrument;
  const avatarUrl = getInstrumentAvatarUrl(avatarInstrument);
  const avatarLabel = avatarInstrument ? `${profileData.name} - ${avatarInstrument}` : `${profileData.name} - Musician`;
  const memberSinceLabel = getMemberSinceLabel(profileData.created);
  const isGenreLimitReached = (formData.genres ?? []).length >= maxGenres;
  const isAvailabilityLimitReached = (formData.availability ?? []).length >= maxGenres;
  const genreOptions = Array.from(new Set([...GENRE_OPTIONS, ...(formData.genres ?? [])]));
  const getGenreButtonClass = (isSelected: boolean, isDisabled: boolean) => {
    const base = 'px-3 py-2 text-sm rounded-md border transition-colors duration-300';
    if (isSelected) return `${base} bg-purple-600 border-purple-500 text-white`;
    if (isDisabled) return `${base} bg-gray-700 border-gray-600 text-gray-500 cursor-not-allowed`;
    return `${base} bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600`;
  };
  
  const handleAvatarError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.src = DEFAULT_AVATAR_URL;
    event.currentTarget.onerror = null;
  };

  const handleContactClick = () => {
    if (!profileData) return;
    navigate('/messages', {
      state: {
        recipientId: profileData.id,
        recipientName: profileData.name,
        recipientInstrument: profileData.instrument,
      },
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {profileError && (
        <div className="mb-4">
          <AlertMessage variant="error">{profileError}</AlertMessage>
        </div>
      )}
      {/* Profile Header */}
      <div className="relative rounded-xl overflow-hidden mb-8">
        <div className="h-64 bg-gradient-to-r from-purple-900 to-indigo-900"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
        
        <div className="absolute bottom-0 left-0 right-0 p-6 flex flex-col md:flex-row justify-between items-start md:items-end">
          <div className="flex items-end">
            <div className="relative w-24 h-24 md:w-32 md:h-32 bg-gray-800 border-4 border-gray-900 overflow-hidden mr-4">
              <img
                src={avatarUrl}
                alt={avatarLabel}
                onError={handleAvatarError}
                className="w-full h-full object-cover"
              />
            </div>
            
            <div>
              {isEditing ? (
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="text-2xl md:text-3xl font-bold text-white bg-gray-800 bg-opacity-50 rounded px-2 py-1 mb-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              ) : (
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{profileData.name}</h1>
              )}
              
              <div className="flex items-center text-gray-300 text-sm md:text-base mb-1">
                <MapPin className="w-4 h-4 mr-1 text-purple-400" />
                <span>{displayedAddress || 'Location not provided'}</span>
              </div>
              
              <div className="flex items-center text-gray-300 text-sm md:text-base">
                <Calendar className="w-4 h-4 mr-1 text-purple-400" />
                <span>{memberSinceLabel}</span>
              </div>
            </div>
          </div>
          
          {isCurrentUser && (
            <div className="mt-4 md:mt-0 flex">
              {isEditing ? (
                <>
                  <button 
                    onClick={handleCancelEdit}
                    className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md transition-colors duration-300 flex items-center mr-2"
                  >
                    <X className="w-5 h-5 mr-1" /> Cancel
                  </button>
                  <button 
                    onClick={handleEditToggle}
                    className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-300 flex items-center"
                  >
                    <Save className="w-5 h-5 mr-1" /> Save
                  </button>
                </>
              ) : (
                <button 
                  onClick={handleEditToggle}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-300 flex items-center"
                >
                  <Edit className="w-5 h-5 mr-1" /> Edit Profile
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Profile Content */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Main Info */}
        <div className="md:col-span-2 space-y-6">
          <motion.div 
            className="bg-gray-800 rounded-lg p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <h2 className="text-xl font-bold text-white mb-4">Musician Info</h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-400 mb-1">Instrument</p>
                {isEditing ? (
                  <select
                    name="instrument"
                    value={formData.instrument}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                  >
                    {INSTRUMENT_OPTIONS.map((instrument) => (
                      <option key={instrument} value={instrument}>
                        {instrument}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="flex items-center">
                    <Music className="w-5 h-5 mr-2 text-purple-500" />
                    <p className="text-lg text-white">{profileData.instrument}</p>
                  </div>
                )}
              </div>
              
              <div>
                <p className="text-sm text-gray-400 mb-1">Skill Level</p>
                {isEditing ? (
                  <select
                    name="skillLevel"
                    value={formData.skillLevel}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                  >
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                    <option value="Professional">Professional</option>
                  </select>
                ) : (
                  <div className="flex items-center">
                    <div className="flex mr-2">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${
                            i < skillStars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-lg text-white">{profileData.skillLevel}</p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm text-gray-400 mb-1">Birth Year</p>
                {isEditing ? (
                  <input
                    type="number"
                    name="birthYear"
                    min="1900"
                    max={new Date().getFullYear()}
                    value={formData.birthYear ?? ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                    placeholder="1995"
                  />
                ) : (
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-purple-500" />
                    <p className="text-lg text-white">{displayedBirthYear ?? 'Not provided'}</p>
                  </div>
                )}
                {displayedAge !== null && (
                  <p className="text-xs text-gray-400 mt-1">Age {displayedAge}</p>
                )}
              </div>

              <div>
                <p className="text-sm text-gray-400 mb-1">Location</p>
                {isEditing ? (
                  <div className="space-y-3">
                    {formData.address ? (
                      <p className="text-xs text-gray-400">Current: {formData.address}</p>
                    ) : (
                      <p className="text-xs text-gray-500">No location selected yet.</p>
                    )}
                    <LocationSelector onLocationSelect={handleLocationSelect} />
                  </div>
                ) : (
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 mr-2 text-purple-500" />
                    <p className="text-lg text-white">{profileData.address || 'Location not provided'}</p>
                  </div>
                )}
              </div>
              
            </div>
          </motion.div>
          
          <motion.div 
            className="bg-gray-800 rounded-lg p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <h2 className="text-xl font-bold text-white mb-4">Music Samples</h2>
            <p className="text-gray-400">No music samples uploaded yet.</p>
            
            {isCurrentUser && (
              <button className="mt-4 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors duration-300 text-sm">
                Upload Music Sample
              </button>
            )}
          </motion.div>
        </div>
        
        {/* Sidebar */}
        <div className="space-y-6">
          {!isCurrentUser && (
            <motion.div 
              className="bg-gray-800 rounded-lg p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="text-xl font-bold text-white mb-4">Contact</h2>
              <button
                onClick={handleContactClick}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-300 flex items-center justify-center"
              >
                <Mail className="w-5 h-5 mr-2" /> Send Message
              </button>
            </motion.div>
          )}
          
          <motion.div 
            className="bg-gray-800 rounded-lg p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <h2 className="text-xl font-bold text-white mb-4">Preferred Genres</h2>
            {isEditing ? (
              <div className="space-y-3">
                <p className="text-xs text-gray-400">Select 1 to {maxGenres} genres.</p>
                <div className="grid grid-cols-2 gap-2">
                  {genreOptions.map((genre) => {
                    const isSelected = (formData.genres ?? []).includes(genre);
                    const isDisabled = !isSelected && isGenreLimitReached;
                    return (
                      <button
                        key={genre}
                        type="button"
                        onClick={() => handleGenreToggle(genre)}
                        disabled={isDisabled}
                        className={getGenreButtonClass(isSelected, isDisabled)}
                        aria-pressed={isSelected}
                      >
                        {genre}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400">{(formData.genres ?? []).length} / {maxGenres} selected</p>
              </div>
            ) : displayedGenres.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {displayedGenres.map((genre) => (
                  <span key={genre} className="px-3 py-1 bg-gray-700 text-white text-sm rounded-full">
                    {genre}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No genres added yet.</p>
            )}
          </motion.div>
          
          <motion.div 
            className="bg-gray-800 rounded-lg p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <h2 className="text-xl font-bold text-white mb-4">Availability</h2>
            {isEditing ? (
              <div className="space-y-3">
                <p className="text-xs text-gray-400">Select 1 to {maxGenres} options.</p>
                <div className="grid grid-cols-1 gap-2">
                  {AVAILABILITY_OPTIONS.map((option) => {
                    const isSelected = (formData.availability ?? []).includes(option);
                    const isDisabled = !isSelected && isAvailabilityLimitReached;
                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => handleAvailabilityToggle(option)}
                        disabled={isDisabled}
                        className={getGenreButtonClass(isSelected, isDisabled)}
                        aria-pressed={isSelected}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400">{(formData.availability ?? []).length} / {maxGenres} selected</p>
              </div>
            ) : displayedAvailability.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {displayedAvailability.map((option) => (
                  <span key={option} className="px-3 py-1 bg-gray-700 text-white text-sm rounded-full">
                    {option}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No availability added yet.</p>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
